import React, { useState, useEffect } from 'react';
import { isAssembleeGenerale, getReunionTypeShort, formatReunionElectionLabel } from '../utils/reunionTypes';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useOrganisme } from '../context/OrganismeContext';
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const SAR_QUARTS = ['1er_quart', '2e_quart', '3e_quart', '4e_quart'];
const SAR_ORDRES_PAR_QUART = [1, 2, 3, 4];
const SAR_CONSEIL_POSTES = SAR_QUARTS.length * SAR_ORDRES_PAR_QUART.length;

export default function ModifierCompositionSAR() {
  const navigate = useNavigate();
  const { organismeActif } = useOrganisme();
  
  const [composition, setComposition] = useState({ actifs: [], inactifs: [] });
  const [adherents, setAdherents] = useState([]);
  const [reunions, setReunions] = useState([]);
  const [, setFractions] = useState([]);
  const [, setFonctions] = useState([]);
  const [periodesEnCours, setPeriodesEnCours] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [modificationsBureau, setModificationsBureau] = useState({ ajouts: [], retraits: [], modifications: [] });
  const [modificationsConseil, setModificationsConseil] = useState({ ajouts: [], retraits: [], modifications: [] });
  
  const [showAjoutBureauModal, setShowAjoutBureauModal] = useState(false);
  const [showAjoutConseilModal, setShowAjoutConseilModal] = useState(false);
  const [showRetraitModal, setShowRetraitModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAdherent, setSelectedAdherent] = useState(null);
  
  const [ajoutForm, setAjoutForm] = useState({
    type: '',
    fonction: '',
    fraction: '',
    ordre: null,
    reunionElection: '',
  });
  
  const [retraitForm, setRetraitForm] = useState({
    membreId: null,
    raisonRetrait: '',
    raisonRetraitAutre: '',
    dateRetrait: new Date().toISOString().split('T')[0],
  });
  
  const [generatePdf, setGeneratePdf] = useState(false);
  const [activeSection, setActiveSection] = useState('conseil');

  useEffect(() => {
    if (organismeActif !== 'SAR') {
      navigate('/admin/composition-conseil');
      return;
    }
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organismeActif]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const headers = { Authorization: `Bearer ${token}` };
      
      const [compositionRes, adherentsRes, reunionsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/conseil/${organismeActif}?includeInactifs=true`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/eligibles`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/reunions`, { headers })
      ]);

      setComposition({
        actifs: compositionRes.data.actifs,
        inactifs: compositionRes.data.inactifs,
      });
      setFractions(compositionRes.data.fractions);
      setFonctions(compositionRes.data.fonctions);
      setPeriodesEnCours(compositionRes.data.periodesEnCours || {});
      setAdherents(adherentsRes.data);
      setReunions(reunionsRes.data.filter(r => isAssembleeGenerale(r.type) || r.type === 'conseil_syndical'));
      
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      alert('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const membresBureau = composition.actifs.filter(m => m.type === 'bureau');
  const membresConseil = composition.actifs.filter(m => m.type === 'conseil');

  const getFilteredAdherents = () => {
    if (!searchTerm) return [];
    
    const searchLower = searchTerm.toLowerCase();
    const baseFiltered = adherents.filter(adherent => {
      return (
        adherent.nom?.toLowerCase().includes(searchLower) ||
        adherent.prenom?.toLowerCase().includes(searchLower) ||
        adherent.email?.toLowerCase().includes(searchLower)
      );
    });

    // Déterminer le type de corps actuel selon le modal ouvert
    let typeCorps = null;
    if (showAjoutBureauModal) typeCorps = 'bureau';
    else if (showAjoutConseilModal) typeCorps = 'conseil';

    if (!typeCorps) return baseFiltered.slice(0, 10);

    // Filtrer les adhérents déjà présents dans ce corps (sauf s'ils sont en retrait)
    return baseFiltered.filter(adherent => {
      // Vérifier si l'adhérent est déjà dans ce corps
      const dejaPresent = composition.actifs.find(m => 
        m.type === typeCorps && 
        m.adherent?._id === adherent._id
      );

      if (!dejaPresent) return true; // Pas présent, on peut l'ajouter

      // Si présent, vérifier s'il est marqué pour retrait
      let retraits = [];
      if (typeCorps === 'bureau') retraits = modificationsBureau.retraits;
      else if (typeCorps === 'conseil') retraits = modificationsConseil.retraits;

      const enRetrait = retraits.some(r => r.membreId === dejaPresent._id);
      return enRetrait; // Autoriser seulement si en retrait
    }).slice(0, 10);
  };

  const filteredAdherents = getFilteredAdherents();

  const getReunionLabel = (reunionId) => {
    const reunion = reunions.find(r => r._id === reunionId);
    if (!reunion) return '-';
    return `${getReunionTypeShort(reunion.type, 'SAR')} ${new Date(reunion.date).toLocaleDateString('fr-FR')}`;
  };

  const getPeriodeLabel = (fraction) => {
    const periode = periodesEnCours[fraction];
    if (!periode) return '-';
    return `${periode.debut}-${periode.fin}`;
  };

  const getTotalModifications = () => {
    return modificationsBureau.ajouts.length + modificationsBureau.retraits.length +
           modificationsConseil.ajouts.length + modificationsConseil.retraits.length;
  };

  const handleOpenAjoutBureau = (fonction) => {
    setAjoutForm({
      type: 'bureau',
      fonction,
      fraction: '',
      ordre: null,
      reunionElection: '',
    });
    setShowAjoutBureauModal(true);
  };

  const handleOpenAjoutConseil = (fraction, ordre) => {
    setAjoutForm({
      type: 'conseil',
      fonction: '',
      fraction,
      ordre,
      reunionElection: '',
    });
    setShowAjoutConseilModal(true);
  };

  const handleConfirmAjoutBureau = () => {
    if (!selectedAdherent || !ajoutForm.reunionElection) return;

    const reunion = reunions.find(r => r._id === ajoutForm.reunionElection);
    
    setModificationsBureau({
      ...modificationsBureau,
      ajouts: [...modificationsBureau.ajouts, {
        adherent: selectedAdherent,
        fonction: ajoutForm.fonction,
        reunionElection: reunion,
        type: 'bureau',
      }]
    });

    setShowAjoutBureauModal(false);
    setSelectedAdherent(null);
    setSearchTerm('');
    setAjoutForm({
      type: '',
      fonction: '',
      fraction: '',
      ordre: null,
      reunionElection: '',
    });
  };

  const handleConfirmAjoutConseil = () => {
    if (!selectedAdherent || !ajoutForm.reunionElection) return;

    const reunion = reunions.find(r => r._id === ajoutForm.reunionElection);
    
    setModificationsConseil({
      ...modificationsConseil,
      ajouts: [...modificationsConseil.ajouts, {
        adherent: selectedAdherent,
        fraction: ajoutForm.fraction,
        ordre: ajoutForm.ordre,
        reunionElection: reunion,
        type: 'conseil',
      }]
    });

    setShowAjoutConseilModal(false);
    setSelectedAdherent(null);
    setSearchTerm('');
    setAjoutForm({
      type: '',
      fonction: '',
      fraction: '',
      ordre: null,
      reunionElection: '',
    });
  };

  const handleOpenRetrait = (membre) => {
    setRetraitForm({
      membreId: membre._id,
      raisonRetrait: '',
      raisonRetraitAutre: '',
      dateRetrait: new Date().toISOString().split('T')[0],
    });
    setShowRetraitModal(true);
  };

  const handleConfirmRetrait = () => {
    const membre = composition.actifs.find(m => m._id === retraitForm.membreId);
    if (!membre) return;

    if (membre.type === 'bureau') {
      setModificationsBureau({
        ...modificationsBureau,
        retraits: [...modificationsBureau.retraits, {
          membreId: membre._id,
          raisonRetrait: retraitForm.raisonRetrait,
          raisonRetraitAutre: retraitForm.raisonRetraitAutre,
          dateRetrait: retraitForm.dateRetrait,
        }]
      });
    } else if (membre.type === 'conseil') {
      setModificationsConseil({
        ...modificationsConseil,
        retraits: [...modificationsConseil.retraits, {
          membreId: membre._id,
          raisonRetrait: retraitForm.raisonRetrait,
          raisonRetraitAutre: retraitForm.raisonRetraitAutre,
          dateRetrait: retraitForm.dateRetrait,
        }]
      });
    }

    setShowRetraitModal(false);
    setRetraitForm({
      membreId: null,
      raisonRetrait: '',
      raisonRetraitAutre: '',
      dateRetrait: new Date().toISOString().split('T')[0],
    });
  };

  const handleCancelAjout = (section, index) => {
    if (section === 'bureau') {
      const newAjouts = [...modificationsBureau.ajouts];
      newAjouts.splice(index, 1);
      setModificationsBureau({ ...modificationsBureau, ajouts: newAjouts });
    } else if (section === 'conseil') {
      const newAjouts = [...modificationsConseil.ajouts];
      newAjouts.splice(index, 1);
      setModificationsConseil({ ...modificationsConseil, ajouts: newAjouts });
    }
  };

  const handleCancelRetrait = (section, index) => {
    if (section === 'bureau') {
      const newRetraits = [...modificationsBureau.retraits];
      newRetraits.splice(index, 1);
      setModificationsBureau({ ...modificationsBureau, retraits: newRetraits });
    } else if (section === 'conseil') {
      const newRetraits = [...modificationsConseil.retraits];
      newRetraits.splice(index, 1);
      setModificationsConseil({ ...modificationsConseil, retraits: newRetraits });
    }
  };

  const handleConfirmSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const allModifications = {
        ajouts: [
          ...modificationsBureau.ajouts.map(a => ({
            adherentId: a.adherent._id,
            type: 'bureau',
            fonction: a.fonction,
            reunionElectionId: a.reunionElection._id,
          })),
          ...modificationsConseil.ajouts.map(a => ({
            adherentId: a.adherent._id,
            type: 'conseil',
            fraction: a.fraction,
            ordre: a.ordre,
            reunionElectionId: a.reunionElection._id,
          })),
        ],
        retraits: [
          ...modificationsBureau.retraits,
          ...modificationsConseil.retraits,
        ],
      };

      await axios.put(
        `${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/masse`,
        { ...allModifications, generatePdf },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(
        generatePdf
          ? 'Enregistrement effectué. Le PDF a été archivé dans « Documents de composition ».'
          : getTotalModifications() > 0
            ? 'Modifications enregistrées avec succès'
            : 'Aucune modification à enregistrer.'
      );
      navigate('/admin/composition-conseil');
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde des modifications');
    } finally {
      setSaving(false);
      setShowConfirmModal(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/composition-conseil')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Retour à la composition
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Modifier la composition - SAR</h1>
              <p className="mt-2 text-gray-600">
                Gérez l'ensemble de la composition du conseil syndical
              </p>
            </div>
            <button
              onClick={() => setShowConfirmModal(true)}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
            >
              {getTotalModifications() === 0
                ? 'Enregistrer'
                : `Enregistrer (${getTotalModifications()} modification${getTotalModifications() > 1 ? 's' : ''})`}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveSection('conseil')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeSection === 'conseil'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Conseil syndical ({SAR_CONSEIL_POSTES} postes)
                {(modificationsConseil.ajouts.length + modificationsConseil.retraits.length) > 0 && (
                  <span className="ml-2 px-2 py-1 text-xs bg-indigo-100 text-indigo-600 rounded-full">
                    {modificationsConseil.ajouts.length + modificationsConseil.retraits.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveSection('bureau')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeSection === 'bureau'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Bureau (7 postes)
                {(modificationsBureau.ajouts.length + modificationsBureau.retraits.length) > 0 && (
                  <span className="ml-2 px-2 py-1 text-xs bg-indigo-100 text-indigo-600 rounded-full">
                    {modificationsBureau.ajouts.length + modificationsBureau.retraits.length}
                  </span>
                )}
              </button>
            </nav>
          </div>
        </div>

        {activeSection === 'bureau' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Bureau (7 postes)</h2>
              <p className="mt-1 text-sm text-gray-600">
                Président, 2 vice-présidents, secrétaire, secrétaire-adjoint, trésorier, trésorier-adjoint
              </p>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fonction</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adhérent</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Début</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {[
                      'president',
                      'vice_president',
                      'vice_president_2',
                      'secretaire',
                      'secretaire_adjoint',
                      'tresorier',
                      'tresorier_adjoint'
                    ].map((fonction) => {
                      const membre = membresBureau.find(m => m.fonction === fonction);
                      const ajoutEnCours = modificationsBureau.ajouts.find(a => a.fonction === fonction);
                      const retraitEnCours = modificationsBureau.retraits.find(r => r.membreId === membre?._id);
                      
                      const fonctionLabels = {
                        president: 'Président',
                        vice_president: 'Vice-président',
                        vice_president_2: 'Vice-président 2',
                        secretaire: 'Secrétaire',
                        secretaire_adjoint: 'Secrétaire adjoint',
                        tresorier: 'Trésorier',
                        tresorier_adjoint: 'Trésorier adjoint',
                      };

                      return (
                        <tr key={fonction} className={!membre && !ajoutEnCours ? 'bg-red-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {fonctionLabels[fonction]}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {ajoutEnCours ? (
                              <div className="flex items-center">
                                <span className="text-green-600 font-medium">
                                  {ajoutEnCours.adherent.prenom} {ajoutEnCours.adherent.nom.toUpperCase()}
                                </span>
                                <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                                  Ajout en cours
                                </span>
                              </div>
                            ) : retraitEnCours ? (
                              <div className="flex items-center">
                                <span className="text-red-600 line-through">
                                  {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                                </span>
                                <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                  Retrait en cours
                                </span>
                              </div>
                            ) : membre ? (
                              <span className="text-gray-900">
                                {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                              </span>
                            ) : (
                              <span className="text-gray-400 italic">Vacant</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {ajoutEnCours ? (
                              getReunionLabel(ajoutEnCours.reunionElection)
                            ) : membre?.reunionElection?.date ? (
                              formatReunionElectionLabel(membre.reunionElection, 'SAR')
                            ) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {ajoutEnCours ? (
                              <button
                                onClick={() => handleCancelAjout('bureau', modificationsBureau.ajouts.indexOf(ajoutEnCours))}
                                className="text-red-600 hover:text-red-900"
                              >
                                Annuler l'ajout
                              </button>
                            ) : retraitEnCours ? (
                              <button
                                onClick={() => handleCancelRetrait('bureau', modificationsBureau.retraits.indexOf(retraitEnCours))}
                                className="text-red-600 hover:text-red-900"
                              >
                                Annuler le retrait
                              </button>
                            ) : membre ? (
                              <button
                                onClick={() => handleOpenRetrait(membre)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Retirer
                              </button>
                            ) : (
                              <button
                                onClick={() => handleOpenAjoutBureau(fonction)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                Ajouter
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'conseil' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Conseil syndical ({SAR_CONSEIL_POSTES} postes)</h2>
              <p className="mt-1 text-sm text-gray-600">
                4 quarts de 4 administrateurs
              </p>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adhérent</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Période</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Début</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {SAR_QUARTS.map((quart, quartIndex) => (
                      <React.Fragment key={quart}>
                        {SAR_ORDRES_PAR_QUART.map((ordre) => {
                          const membre = membresConseil.find(m => m.fraction === quart && m.ordre === ordre);
                          const ajoutEnCours = modificationsConseil.ajouts.find(a => a.fraction === quart && a.ordre === ordre);
                          const retraitEnCours = modificationsConseil.retraits.find(r => r.membreId === membre?._id);
                          
                          return (
                            <tr key={`${quart}-${ordre}`} className={!membre && !ajoutEnCours ? 'bg-red-50' : ''}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {quart.replace('_', ' ')} / {ordre}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {ajoutEnCours ? (
                                  <div className="flex items-center">
                                    <span className="text-green-600 font-medium">
                                      {ajoutEnCours.adherent.prenom} {ajoutEnCours.adherent.nom.toUpperCase()}
                                    </span>
                                    <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                                      Ajout en cours
                                    </span>
                                  </div>
                                ) : retraitEnCours ? (
                                  <div className="flex items-center">
                                    <span className="text-red-600 line-through">
                                      {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                                    </span>
                                    <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                      Retrait en cours
                                    </span>
                                  </div>
                                ) : membre ? (
                                  <span className="text-gray-900">
                                    {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 italic">Vacant</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {getPeriodeLabel(quart)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {ajoutEnCours ? (
                                  getReunionLabel(ajoutEnCours.reunionElection)
                                ) : membre?.reunionElection?.date ? (
                                  formatReunionElectionLabel(membre.reunionElection, 'SAR')
                                ) : '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {ajoutEnCours ? (
                                  <button
                                    onClick={() => handleCancelAjout('conseil', modificationsConseil.ajouts.indexOf(ajoutEnCours))}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    Annuler l'ajout
                                  </button>
                                ) : retraitEnCours ? (
                                  <button
                                    onClick={() => handleCancelRetrait('conseil', modificationsConseil.retraits.indexOf(retraitEnCours))}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    Annuler le retrait
                                  </button>
                                ) : membre ? (
                                  <button
                                    onClick={() => handleOpenRetrait(membre)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    Retirer
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleOpenAjoutConseil(quart, ordre)}
                                    className="text-indigo-600 hover:text-indigo-900"
                                  >
                                    Ajouter
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {quartIndex < 3 && (
                          <tr className="bg-gray-100">
                            <td colSpan="5" className="px-6 py-2"></td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {showAjoutBureauModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Ajouter un membre au Bureau
                </h3>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rechercher un adhérent *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Nom ou prénom..."
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  
                  {searchTerm && filteredAdherents.length > 0 && (
                    <div className="mt-2 border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                      {filteredAdherents.map((adherent) => (
                        <button
                          key={adherent._id}
                          onClick={() => {
                            setSelectedAdherent(adherent);
                            setSearchTerm('');
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">
                            {adherent.prenom} {adherent.nom}
                          </div>
                          <div className="text-sm text-gray-500">
                            {adherent.email}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {selectedAdherent && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                      <div>
                        <div className="font-medium text-green-900">
                          {selectedAdherent.prenom} {selectedAdherent.nom}
                        </div>
                        <div className="text-sm text-green-700">
                          {selectedAdherent.email}
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedAdherent(null)}
                        className="text-green-600 hover:text-green-800"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Réunion d'élection *
                  </label>
                  <select
                    value={ajoutForm.reunionElection}
                    onChange={(e) => setAjoutForm({ ...ajoutForm, reunionElection: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  >
                    <option value="">Sélectionner une réunion</option>
                    {reunions.map((reunion) => (
                      <option key={reunion._id} value={reunion._id}>
                        {getReunionTypeShort(reunion.type, 'SAR')} - {new Date(reunion.date).toLocaleDateString('fr-FR')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowAjoutBureauModal(false);
                    setSelectedAdherent(null);
                    setSearchTerm('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmAjoutBureau}
                  disabled={!selectedAdherent || !ajoutForm.reunionElection}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        )}

        {showAjoutConseilModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Ajouter un membre au Conseil
                </h3>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rechercher un adhérent *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Nom ou prénom..."
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  
                  {searchTerm && filteredAdherents.length > 0 && (
                    <div className="mt-2 border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                      {filteredAdherents.map((adherent) => (
                        <button
                          key={adherent._id}
                          onClick={() => {
                            setSelectedAdherent(adherent);
                            setSearchTerm('');
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">
                            {adherent.prenom} {adherent.nom}
                          </div>
                          <div className="text-sm text-gray-500">
                            {adherent.email}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {selectedAdherent && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                      <div>
                        <div className="font-medium text-green-900">
                          {selectedAdherent.prenom} {selectedAdherent.nom}
                        </div>
                        <div className="text-sm text-green-700">
                          {selectedAdherent.email}
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedAdherent(null)}
                        className="text-green-600 hover:text-green-800"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Réunion d'élection *
                  </label>
                  <select
                    value={ajoutForm.reunionElection}
                    onChange={(e) => setAjoutForm({ ...ajoutForm, reunionElection: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  >
                    <option value="">Sélectionner une réunion</option>
                    {reunions.map((reunion) => (
                      <option key={reunion._id} value={reunion._id}>
                        {getReunionTypeShort(reunion.type, 'SAR')} - {new Date(reunion.date).toLocaleDateString('fr-FR')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowAjoutConseilModal(false);
                    setSelectedAdherent(null);
                    setSearchTerm('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmAjoutConseil}
                  disabled={!selectedAdherent || !ajoutForm.reunionElection}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        )}

        {showRetraitModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Retirer un membre
                </h3>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Raison du retrait *
                  </label>
                  <select
                    value={retraitForm.raisonRetrait}
                    onChange={(e) => setRetraitForm({ ...retraitForm, raisonRetrait: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  >
                    <option value="">Sélectionner une raison</option>
                    <option value="demission">Démission</option>
                    <option value="fin_mandat">Fin de mandat</option>
                    <option value="deces">Décès</option>
                    <option value="revocation">Révocation</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                {retraitForm.raisonRetrait === 'autre' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Préciser la raison
                    </label>
                    <textarea
                      value={retraitForm.raisonRetraitAutre}
                      onChange={(e) => setRetraitForm({ ...retraitForm, raisonRetraitAutre: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                      rows="3"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date du retrait *
                  </label>
                  <input
                    type="date"
                    value={retraitForm.dateRetrait}
                    onChange={(e) => setRetraitForm({ ...retraitForm, dateRetrait: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowRetraitModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmRetrait}
                  disabled={!retraitForm.raisonRetrait || !retraitForm.dateRetrait}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirmer le retrait
                </button>
              </div>
            </div>
          </div>
        )}

        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">
                  Composition finale
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  {getTotalModifications() === 0
                    ? 'Aucune modification en cours — vous pouvez archiver la composition actuelle en PDF.'
                    : 'Vérifiez les modifications avant de les enregistrer'}
                </p>
              </div>

              <div className="p-6 space-y-6">
                {(modificationsBureau.ajouts.length > 0 || modificationsBureau.retraits.length > 0) && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Bureau</h4>
                    {modificationsBureau.ajouts.length > 0 && (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-green-700 mb-2">Ajouts :</p>
                        <ul className="space-y-1">
                          {modificationsBureau.ajouts.map((ajout, index) => (
                            <li key={index} className="text-sm text-gray-700">
                              • {ajout.adherent.prenom} {ajout.adherent.nom} - {ajout.fonction}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {modificationsBureau.retraits.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-red-700 mb-2">Retraits :</p>
                        <ul className="space-y-1">
                          {modificationsBureau.retraits.map((retrait, index) => {
                            const membre = composition.actifs.find(m => m._id === retrait.membreId);
                            return (
                              <li key={index} className="text-sm text-gray-700">
                                • {membre?.adherent?.prenom} {membre?.adherent?.nom} - {retrait.raisonRetrait}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {(modificationsConseil.ajouts.length > 0 || modificationsConseil.retraits.length > 0) && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Conseil</h4>
                    {modificationsConseil.ajouts.length > 0 && (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-green-700 mb-2">Ajouts :</p>
                        <ul className="space-y-1">
                          {modificationsConseil.ajouts.map((ajout, index) => (
                            <li key={index} className="text-sm text-gray-700">
                              • {ajout.adherent.prenom} {ajout.adherent.nom} - {ajout.fraction} / {ajout.ordre}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {modificationsConseil.retraits.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-red-700 mb-2">Retraits :</p>
                        <ul className="space-y-1">
                          {modificationsConseil.retraits.map((retrait, index) => {
                            const membre = composition.actifs.find(m => m._id === retrait.membreId);
                            return (
                              <li key={index} className="text-sm text-gray-700">
                                • {membre?.adherent?.prenom} {membre?.adherent?.nom} - {retrait.raisonRetrait}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2 p-4 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="generatePdf"
                    checked={generatePdf}
                    onChange={(e) => setGeneratePdf(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="generatePdf" className="text-sm text-gray-700">
                    Générer un PDF de la composition finale
                  </label>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={saving}
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmSave}
                  disabled={saving}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? 'Enregistrement...' : 'Confirmer et enregistrer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
